import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, UserPlus, Mail, Phone, Calendar, FileText, CheckCircle, Clock, XCircle, X, Loader2 } from 'lucide-react';
import api, { Candidate } from '../services/api';

const HiringPage: React.FC = () => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate | null>(null);
  const [formData, setFormData] = useState<{
    name: string; email: string; phone: string; role: string;
    department: 'marketing' | 'development' | 'design' | 'sales' | 'operations';
    status: 'new' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected';
    experience: string; skills: string[]; resume_url: string; notes: string;
    applied_date: string; salary_expectation: string;
  }>({
    name: '', email: '', phone: '', role: '', department: 'marketing', status: 'new',
    experience: '', skills: [], resume_url: '', notes: '',
    applied_date: new Date().toISOString().split('T')[0], salary_expectation: ''
  });
  const [skillInput, setSkillInput] = useState('');

  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getCandidates({
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
        search: searchQuery || undefined
      });
      setCandidates(response.results);
      setError(null);
    } catch (err) {
      setError('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, selectedDepartment, searchQuery]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = { ...formData, salary_expectation: formData.salary_expectation || null };
      if (editingCandidate) {
        await api.updateCandidate(editingCandidate.id, data);
      } else {
        await api.createCandidate(data);
      }
      setShowModal(false);
      setEditingCandidate(null);
      resetForm();
      fetchCandidates();
    } catch (err) {
      alert('Failed to save candidate');
    }
  };

  const handleEdit = (candidate: Candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      name: candidate.name, email: candidate.email, phone: candidate.phone, role: candidate.role,
      department: candidate.department as any, status: candidate.status as any,
      experience: candidate.experience, skills: candidate.skills || [], resume_url: candidate.resume_url,
      notes: candidate.notes, applied_date: candidate.applied_date, salary_expectation: candidate.salary_expectation || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this candidate?')) {
      try { await api.deleteCandidate(id); fetchCandidates(); }
      catch { alert('Failed to delete candidate'); }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', email: '', phone: '', role: '', department: 'marketing', status: 'new',
      experience: '', skills: [], resume_url: '', notes: '',
      applied_date: new Date().toISOString().split('T')[0], salary_expectation: ''
    });
    setSkillInput('');
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, skillInput.trim()] });
      setSkillInput('');
    }
  };

  const removeSkill = (skill: string) => setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      'new': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'screening': 'status-badge status-warning',
      'interview': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      'offer': 'status-badge status-active',
      'hired': 'status-badge status-active',
      'rejected': 'status-badge status-error'
    };
    return map[status] || 'status-badge';
  };

  const getStatusIcon = (status: string) => {
    const map: Record<string, React.ReactNode> = {
      'new': <UserPlus className="w-3 h-3" />,
      'screening': <Clock className="w-3 h-3" />,
      'interview': <Calendar className="w-3 h-3" />,
      'offer': <CheckCircle className="w-3 h-3" />,
      'hired': <CheckCircle className="w-3 h-3" />,
      'rejected': <XCircle className="w-3 h-3" />
    };
    return map[status] || null;
  };

  const getDepartmentDisplay = (dept: string) => {
    const map: Record<string, string> = { 'marketing': 'Marketing', 'development': 'Dev', 'design': 'Design', 'sales': 'Sales', 'operations': 'Ops' };
    return map[dept] || dept;
  };

  if (loading && candidates.length === 0) {
    return <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">Hiring</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Manage candidates</p>
            </div>
            <button onClick={() => { resetForm(); setEditingCandidate(null); setShowModal(true); }} className="btn btn-primary">
              <Plus className="w-4 h-4" />Add Candidate
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Error */}
        {error && <div className="bg-status-error/10 border border-status-error/30 rounded p-2 text-xs text-status-error mb-2">{error}</div>}

        {/* Filters - Compact */}
        <div className="filter-bar mb-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" placeholder="Search..." className="input pl-7" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <select className="select" value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
            <option value="all">All Depts</option>
            <option value="marketing">Marketing</option>
            <option value="development">Dev</option>
            <option value="design">Design</option>
            <option value="sales">Sales</option>
            <option value="operations">Ops</option>
          </select>
          <select className="select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="screening">Screening</option>
            <option value="interview">Interview</option>
            <option value="offer">Offer</option>
            <option value="hired">Hired</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Candidates Grid - Compact */}
        {candidates.length === 0 ? (
          <div className="text-center py-8"><p className="text-xs text-gray-500 dark:text-gray-400">No candidates found</p></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {candidates.map((candidate) => (
              <div key={candidate.id} className="panel">
                <div className="p-3">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{candidate.name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusClass(candidate.status)}`}>
                          {getStatusIcon(candidate.status)}{candidate.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-300">{candidate.role}</p>
                      <p className="text-xs text-gray-500">{getDepartmentDisplay(candidate.department)}</p>
                    </div>
                    <button onClick={() => handleEdit(candidate)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                      <FileText className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-2 text-xs">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Mail className="w-3 h-3 text-gray-400" /><span className="truncate">{candidate.email}</span>
                    </div>
                    {candidate.phone && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Phone className="w-3 h-3 text-gray-400" /><span>{candidate.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Calendar className="w-3 h-3 text-gray-400" />
                      <span>{new Date(candidate.applied_date).toLocaleDateString()}</span>
                    </div>
                    {candidate.experience && <div className="text-gray-600 dark:text-gray-300">{candidate.experience}</div>}
                  </div>

                  {/* Skills */}
                  {candidate.skills && candidate.skills.length > 0 && (
                    <div className="mb-2">
                      <div className="flex flex-wrap gap-1">
                        {candidate.skills.slice(0, 4).map((skill, idx) => (
                          <span key={idx} className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">{skill}</span>
                        ))}
                        {candidate.skills.length > 4 && <span className="text-xs text-gray-500">+{candidate.skills.length - 4}</span>}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-1 border-t border-light-border dark:border-dark-border pt-2">
                    <button onClick={() => handleEdit(candidate)} className="btn btn-ghost text-blue-600">Edit</button>
                    <button onClick={() => handleDelete(candidate.id)} className="btn btn-ghost text-red-600">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal - Compact */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between p-3 border-b border-light-border dark:border-dark-border">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{editingCandidate ? 'Edit Candidate' : 'Add Candidate'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-3 space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                  <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                <input type="text" required value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
                  <select required value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value as any })} className="select">{['marketing', 'development', 'design', 'sales', 'operations'].map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}</select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value as any })} className="select">{['new', 'screening', 'interview', 'offer', 'hired', 'rejected'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}</select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Experience</label>
                  <input type="text" placeholder="e.g., 5 years" value={formData.experience} onChange={(e) => setFormData({ ...formData, experience: e.target.value })} className="input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Applied Date</label>
                  <input type="date" required value={formData.applied_date} onChange={(e) => setFormData({ ...formData, applied_date: e.target.value })} className="input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Skills</label>
                <div className="flex gap-1 mb-1">
                  <input type="text" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())} className="input flex-1" placeholder="Add skill" />
                  <button type="button" onClick={addSkill} className="btn btn-secondary">Add</button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {formData.skills.map((skill, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                      {skill}<button type="button" onClick={() => removeSkill(skill)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Salary Expectation</label>
                <input type="number" step="0.01" value={formData.salary_expectation} onChange={(e) => setFormData({ ...formData, salary_expectation: e.target.value })} className="input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input" rows={2} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingCandidate ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HiringPage;
